import { useEffect, useRef } from 'react';

import { useRecoilValue } from 'recoil';

import { isKioskDevice } from 'lib/helpers/kiosk';

import { userState } from 'components/state';
import { currentPractice } from 'components/practice/practiceState';
import { useSubModal } from 'components/shared/Modal/components/SubModal/lib/SubModalContext';
import { useCheckinFlowContext } from 'components/Patient/checkin/Wizard/lib/checkinFlowContext';

import ReceiptSheet from './components/ReceiptSheet';
import ReceiptSheetActions from './components/ReceiptSheetActions';

// Inside an open shared Modal, present the receipt through its SubModal instead of
// stacking a second overlay; the receipt content mounts fresh per open inside
// ReceiptSheet.
const ReceiptSubModal = ({
  isOpen,
  onClose,
  transactionId,
  appointmentId,
  eobId,
  viewDefaultReceipt = true,
  walkout = false,
  token,
  isPatient: isPatientProp
}) => {
  const { openSubModal, closeSubModal } = useSubModal();

  const practice = useRecoilValue(currentPractice);
  const user = useRecoilValue(userState);
  // no printer on kiosk
  const { kiosk } = useCheckinFlowContext();
  const kioskDevice = kiosk || isKioskDevice();

  const isPatient = isPatientProp ?? (!user.logged_in || (user?.logged_in && !practice?.id));

  const propsRef = useRef({});
  propsRef.current = {
    onClose,
    transactionId,
    appointmentId,
    eobId,
    viewDefaultReceipt,
    walkout,
    token,
    kioskDevice,
    isPatient
  };

  // Holds this sheet's stack id while open, so closing removes our own entry rather
  // than whatever sheet is currently on top.
  const openedRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      if (openedRef.current) {
        closeSubModal(openedRef.current);
        openedRef.current = null;
      }
      return undefined;
    }

    openedRef.current = openSubModal({
      title: 'Receipt',
      footer: false,
      className: 'overflow-hidden bg-white !p-0',
      panelClassName: 'h-full max-h-full',
      content: <ReceiptSheet propsRef={propsRef} />,
      headerActions: <ReceiptSheetActions propsRef={propsRef} />,
      actions: { onClose: () => propsRef.current.onClose?.() }
    });

    return () => {
      if (openedRef.current) {
        closeSubModal(openedRef.current);
        openedRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return null;
};

export default ReceiptSubModal;
